import { Option, OptionGroup } from '@joor/design-system'

import {
  BillingAddress,
  HyperionAddress,
  Location,
  LocationType,
  Maybe,
  OrderAddress,
  ShippingAddress,
} from '__generated__/atlas-types'

import { fromGlobalId, isGlobalId } from 'utils/transformations/graphql'

import { messages } from '../../constants/messages'
import { RetailerLocation } from '../EditAddressesModal/hooks/useAddressModalData'
import { formatOrderAddress } from 'features/Orders/Review/utils/addresses.utils'

// This ID is the same in all the envs
export const UNITED_STATES_ID = 'R2VvZ3JhcGh5OjIwMg=='

export const REQUIRED_FIELD_ERROR_MESSAGE = 'This field is required.'
export const DEFAULT_ORDER_ADDRESS_ID = 'ORDER_ADDRESS_ID'

const DEFAULT_PRIMARY_LOCATION_NAME = 'Primary Location'

export const getCountryOption = (
  countryName: string | undefined | null,
  countryOptions: Option[],
) => countryOptions.find((country) => country.label === countryName)

export const extractCompanyFromLabel = (label: string): string => {
  return label.split('\n')[0]
}

export const formatPrimaryLocationOption = (
  primaryLocation: RetailerLocation | undefined,
): Option => ({
  value: primaryLocation?.id || DEFAULT_PRIMARY_LOCATION_NAME,
  label: formatLabelWithBreakLine(
    primaryLocation?.name ?? DEFAULT_PRIMARY_LOCATION_NAME,
    formatShortRetailerLocation(primaryLocation as Location),
  ),
})

const formatShortRetailerLocation = (location: Location | undefined) => {
  if (!location) {
    return ''
  }
  const { address1, address2, city, country } = location
  const address = [address1, address2].filter(Boolean).join(' ')
  return [address, city, country].filter(Boolean).join(', ')
}

export const cleanupLocationId = (name?: Option | null) => {
  if (
    name &&
    isGlobalId(name?.value) &&
    fromGlobalId(name?.value)?.type?.toLowerCase()?.includes('location')
  ) {
    return ''
  }
  return name?.value
}

export const formatRetailerLocationOptions = (
  retailerLocations: RetailerLocation[],
): Option[] =>
  retailerLocations?.map((location, index) => ({
    label: formatLabelWithBreakLine(
      location?.name ?? `Location ${index + 1}`,
      formatShortRetailerLocation(location as Location),
    ),
    value: location?.id ?? '',
  }))

export const formatOrderAddressOption = (
  orderAddress: OrderAddress | undefined,
): Option => ({
  value: DEFAULT_ORDER_ADDRESS_ID,
  label: orderAddress?.company
    ? formatLabelWithBreakLine(
        orderAddress.company,
        formatShortOrderAddress(orderAddress),
      )
    : formatOrderAddress(orderAddress),
})

const formatLabelWithBreakLine = (company: string, shortAddress: string) => {
  if (!shortAddress) {
    return company
  }
  return `${company}\n${shortAddress}`
}

const formatShortOrderAddress = (orderAddress: OrderAddress | undefined) => {
  if (!orderAddress) {
    return ''
  }

  const { firstAddress, secondAddress, country, city } = orderAddress
  const address = [firstAddress, secondAddress].filter(Boolean).join(' ')
  return [address, city, country].filter(Boolean).join(', ')
}

export const formatBrandAddressOptions = (
  addresses: (BillingAddress | ShippingAddress)[],
): Option[] =>
  addresses
    .filter(({ address }) => Boolean(address?.companyName))
    .map(({ id, address }) => ({
      value: id,
      label: formatLabelWithBreakLine(
        address!.companyName!,
        formatShortBrandAddress(address!),
      ),
    }))

const formatShortBrandAddress = (address: HyperionAddress) => {
  const { line1, line2, city, country } = address
  const addressLine = [line1, line2].filter(Boolean).join(' ')
  return [addressLine, city, country].filter(Boolean).join(', ')
}

export type GetAddressOptionsInputType = {
  brandAddressOptions: Option[]
  orderAddressOption: Option
  retailerLocationsOptions: Option[]
  primaryLocationOption: Option
  shouldAddOrderAddress: boolean
}

export type OptionsRecord = {
  orderOptions: Option[]
  brandOptions: Option[]
  primaryLocationOption: Option[]
  aggregateOptions: Option[]
  retailerLocationsOptions: Option[]
}

export const getAddressOptionsForBrands = ({
  brandAddressOptions,
  orderAddressOption,
  primaryLocationOption,
  shouldAddOrderAddress,
}: GetAddressOptionsInputType): OptionsRecord => ({
  orderOptions: shouldAddOrderAddress ? [orderAddressOption] : [],
  brandOptions: brandAddressOptions,
  primaryLocationOption: [primaryLocationOption],
  retailerLocationsOptions: [],
  aggregateOptions: [
    ...(shouldAddOrderAddress ? [orderAddressOption] : []),
    ...brandAddressOptions,
    primaryLocationOption,
  ],
})

export const getAddressOptionsForLiteRetailers = ({
  brandAddressOptions,
  orderAddressOption,
  primaryLocationOption,
  retailerLocationsOptions,
  shouldAddOrderAddress,
}: GetAddressOptionsInputType): OptionsRecord => {
  // Primary Location and Retailer Locations are both of
  // locations so in must cases we will have to make sure
  // the primary location is removed from the Retailer
  // locations array
  const filterRetailerLocationOptions = (op: Option) =>
    op?.value !== primaryLocationOption?.value

  return {
    orderOptions: shouldAddOrderAddress ? [orderAddressOption] : [],
    brandOptions: brandAddressOptions,
    primaryLocationOption: [primaryLocationOption],
    retailerLocationsOptions:
      retailerLocationsOptions?.filter(filterRetailerLocationOptions) ?? [],
    aggregateOptions: [
      ...(shouldAddOrderAddress ? [orderAddressOption] : []),
      primaryLocationOption,
      ...retailerLocationsOptions?.filter(filterRetailerLocationOptions),
      ...brandAddressOptions,
    ],
  }
}

export const getAddressesOptionGroup = (
  isAccountTypeBrand: boolean,
  options: OptionsRecord,
): OptionGroup[] =>
  isAccountTypeBrand
    ? [
        {
          label: 'This Order',
          options: options.orderOptions,
        },
        {
          label: 'Customer Specific Data',
          options: options.brandOptions,
        },
        {
          label: 'Retailer Primary Address',
          options: options.primaryLocationOption,
        },
      ]
    : [
        {
          label: 'This Order',
          options: options.orderOptions,
        },
        {
          label: 'Primary Location',
          options: options.primaryLocationOption,
        },
        {
          label: 'Your Locations',
          options: options.retailerLocationsOptions,
        },
        {
          label: 'Brand Saved',
          options: options.brandOptions,
        },
      ]

export const getCompanyNameLabel = (type?: Maybe<LocationType>): string => {
  if (type === LocationType.OFFICE) {
    return messages.officeName
  }
  return messages.storeName
}
